import React, { useState, useEffect, useRef } from "react";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "./Logo";
import { navLinks } from "../mock";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kyo_auth") || "null"); }
    catch { return null; }
  });

  useEffect(() => {
    const sync = () => {
      try { setAuthUser(JSON.parse(localStorage.getItem("kyo_auth") || "null")); }
      catch { setAuthUser(null); }
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close on click-outside */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const scrollTo = (href) => {
    setOpen(false);
    if (href.startsWith("/")) { navigate(href); return; }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const logout = () => {
    localStorage.removeItem("kyo_auth");
    setAuthUser(null);
    setOpen(false);
    toast.success("Signed out successfully");
  };

  return (
    <header
      ref={menuRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#1c1e21] shadow-[0_4px_20px_rgba(0,0,0,0.35)]" : "bg-[#212327]"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 lg:px-6">
        <div className="h-[70px] flex items-center justify-between">
          <button onClick={() => navigate("/")} aria-label="KYo home" className="relative">
            <span className="absolute inset-0 -m-4 rounded-full bg-white/10 blur-xl" aria-hidden="true" />
            <span className="relative">
              <Logo />
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <button
                key={l.label}
                onClick={() => scrollTo(l.href)}
                className={`text-[14px] font-medium transition-colors ${
                  l.label === "Home"
                    ? "text-[#ED2E24]"
                    : "text-white/90 hover:text-[#ED2E24]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            {authUser ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="hidden sm:block text-white/60 hover:text-white text-xs max-w-[130px] truncate transition-colors"
                  title="Go to my dashboard"
                  data-testid="navbar-dashboard-link"
                >
                  {authUser.email}
                </button>
                <button
                  onClick={logout}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[12px] font-medium px-3 py-1.5 rounded-md transition-colors"
                  data-testid="navbar-logout-btn"
                  title="Sign out"
                >
                  <LogOut size={13} />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => navigate("/signin")}
                  className="border border-white/30 hover:border-white/60 text-white/80 hover:text-white text-[13px] font-medium px-4 py-2 rounded-md transition-colors"
                  data-testid="navbar-login-btn"
                >
                  Customer Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[13px] font-semibold px-5 py-2 rounded-md transition-colors shadow-sm"
                  data-testid="navbar-signup-btn"
                >
                  Sign up
                </button>
              </div>
            )}

            {/* Animated hamburger / X */}
            <button
              className="lg:hidden relative w-7 h-7 flex items-center justify-center text-white"
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              <span
                className={`absolute transition-all duration-200 ${
                  open ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
                }`}
              >
                <Menu size={24} />
              </span>
              <span
                className={`absolute transition-all duration-200 ${
                  open ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75"
                }`}
              >
                <X size={24} />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Animated mobile menu ── */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          open ? "max-h-[calc(100dvh-70px)] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="mobile-menu"
      >
        <div className="bg-[#1c1e21] border-t border-white/10 px-5 pt-4 pb-6 flex flex-col gap-1 overflow-y-auto max-h-[calc(100dvh-70px)]">

          {/* Nav links */}
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => scrollTo(l.href)}
              className={`text-left text-[15px] font-medium py-2.5 border-b border-white/5 last:border-0 transition-colors ${
                l.label === "Home" ? "text-[#ED2E24]" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}

          {/* Divider */}
          <div className="my-2 border-t border-white/10" />

          {/* Auth section */}
          {authUser ? (
            <div className="flex flex-col gap-2.5 pt-1">
              <button
                onClick={() => { setOpen(false); navigate("/dashboard"); }}
                className="flex items-center gap-2 w-full bg-white/10 hover:bg-white/15 text-white text-[14px] font-medium px-4 py-3 rounded-xl transition-colors"
                data-testid="mobile-dashboard-btn"
              >
                <LayoutDashboard size={16} className="shrink-0 text-white/60" />
                <span className="truncate">{authUser.email}</span>
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 w-full border border-white/15 hover:border-white/30 text-white/70 hover:text-white text-[14px] font-medium px-4 py-3 rounded-xl transition-colors"
                data-testid="mobile-signout-btn"
              >
                <LogOut size={15} />
                Sign out
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5 pt-1">
              <button
                onClick={() => { setOpen(false); navigate("/signin"); }}
                className="w-full border border-white/20 hover:border-white/40 text-white/80 hover:text-white text-[14px] font-medium py-3 rounded-xl transition-colors"
                data-testid="mobile-login-btn"
              >
                Customer Login
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/signup"); }}
                className="w-full bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[14px] font-semibold py-3 rounded-xl transition-colors shadow-sm"
                data-testid="mobile-signup-btn"
              >
                Sign up
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
